import { sortContentGroupsForTileReadingOrder } from '@/helpers/tileRowOrder';
import { isReelLike } from '@/helpers/isReelLike';
import {
  resolveFeedFolderProductsOnce,
  buildFeedFolderDisplayContents,
  buildVirtualGroupsForFeedFolder,
  expandsFeedFolderAsGroups,
} from '@/helpers/feedFolderResolver';

const asArray = (value) => Array.isArray(value) ? value.filter(Boolean) : [];
const TARGETING_RUNTIME_STATE_KEY = '__storeeelRandomAdTargetingAssignments';
const TARGETING_SESSION_STORAGE_VERSION = 1;

const cloneValue = (value) => {
  if (typeof globalThis.structuredClone === 'function') {
    return globalThis.structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value));
};

const normalizeId = (value) => value == null ? '' : String(value);
const FREQUENCY_INSERT_CONTENT_PREFIX = 'frequency-insert-content';

const ensureArrayUnique = (values) => {
  const seen = new Set();
  return values.filter((value) => {
    const normalized = normalizeId(value);
    if (!normalized || seen.has(normalized)) return false;
    seen.add(normalized);
    return true;
  });
};

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value || {}, key);

const normalizeSegmentAnswers = (answers) => Object.entries(answers || {}).reduce((result, [key, value]) => {
  const normalizedKey = normalizeId(key);
  const normalizedValue = normalizeId(value);
  if (!normalizedKey || !normalizedValue) return result;
  result[normalizedKey] = normalizedValue;
  return result;
}, {});

const normalizeStoredAnswerValues = (values) => {
  if (Array.isArray(values)) return ensureArrayUnique(values);
  if (values && typeof values === 'object') {
    return ensureArrayUnique(Object.values(values).flatMap((value) => asArray(value).length > 0 ? asArray(value) : [value]));
  }
  return [];
};

const normalizeStoredAnswerMap = (values) => {
  if (!values || Array.isArray(values) || typeof values !== 'object') return {};

  return Object.entries(values).reduce((result, [key, value]) => {
    const normalizedKey = normalizeId(key);
    const normalizedValues = ensureArrayUnique(asArray(value).length > 0 ? asArray(value) : [value]);
    if (!normalizedKey || normalizedValues.length === 0) return result;
    result[normalizedKey] = normalizedValues;
    return result;
  }, {});
};

const readRuntimeAssignments = () => {
  try {
    const raw = globalThis[TARGETING_RUNTIME_STATE_KEY];
    if (!raw || typeof raw !== 'object') return {
      version: TARGETING_SESSION_STORAGE_VERSION,
      tenantId: '',
      currentUrl: '',
      campaigns: {},
    };

    if (raw?.version !== TARGETING_SESSION_STORAGE_VERSION) {
      return {
        version: TARGETING_SESSION_STORAGE_VERSION,
        tenantId: '',
        currentUrl: '',
        campaigns: {},
      };
    }

    return {
      version: TARGETING_SESSION_STORAGE_VERSION,
      tenantId: normalizeId(raw?.tenantId),
      currentUrl: normalizeId(raw?.currentUrl),
      campaigns: raw?.campaigns && typeof raw.campaigns === 'object' ? raw.campaigns : {},
    };
  } catch {
    return {
      version: TARGETING_SESSION_STORAGE_VERSION,
      tenantId: '',
      currentUrl: '',
      campaigns: {},
    };
  }
};

const writeRuntimeAssignments = (value) => {
  try {
    globalThis[TARGETING_RUNTIME_STATE_KEY] = value;
  } catch {
    void 0;
  }
};

const normalizeStoredAssetIds = (value) => ensureArrayUnique(asArray(value));
const normalizeStoredDisplayCounts = (value) => Object.entries(value && typeof value === 'object' ? value : {}).reduce((result, [key, count]) => {
  const normalizedKey = normalizeId(key);
  const normalizedCount = Number(count);
  if (!normalizedKey || !Number.isFinite(normalizedCount) || normalizedCount <= 0) return result;
  result[normalizedKey] = normalizedCount;
  return result;
}, {});
const normalizeStoredDisplayOrders = (value) => Object.entries(value && typeof value === 'object' ? value : {}).reduce((result, [key, order]) => {
  const normalizedKey = normalizeId(key);
  const normalizedOrder = Number(order);
  if (!normalizedKey || !Number.isFinite(normalizedOrder) || normalizedOrder <= 0) return result;
  result[normalizedKey] = normalizedOrder;
  return result;
}, {});

const getSegmentOptionStorageValue = (option) => normalizeId(
  option?.segmentValueName
  || option?.label
  || option?.segmentValueId
);

const findSegmentOptionByStoredValue = (group, storedValue) => {
  const normalizedStoredValue = normalizeId(storedValue);
  if (!normalizedStoredValue) return null;

  return asArray(group?.questionnaire?.options).find((option) => (
    normalizeId(option?.segmentValueId) === normalizedStoredValue
    || getSegmentOptionStorageValue(option) === normalizedStoredValue
    || normalizeId(option?.label) === normalizedStoredValue
  ));
};

export const normalizeTargetingAnswers = (answers = {}) => {
  // 'tags' はタグ機能廃止前に保存された回答。キーの有無で新旧形式を判別するためだけに見る
  if (hasOwn(answers, 'segments') || hasOwn(answers, 'tags')) {
    return { segments: normalizeSegmentAnswers(answers?.segments) };
  }

  // 旧形式の segment 回答も読み込めるようにする
  return { segments: normalizeSegmentAnswers(answers) };
};

export const mergeTargetingAnswers = (...answersList) => answersList.reduce((result, answers) => {
  const normalized = normalizeTargetingAnswers(answers);
  return {
    segments: {
      ...result.segments,
      ...normalized.segments,
    },
  };
}, { segments: {} });

const createTargetingLookups = () => ({
  campaignById: new Map(),
  contentGroupById: new Map(),
  contentById: new Map(),
  mediaAssetById: new Map(),
  adMediaAssetById: new Map(),
  segmentValueById: new Map(),
  segmentKeyById: new Map(),
});

const buildParsedSegmentValue = (segmentValue) => {
  const segmentKey = segmentValue?.segmentKey;
  return {
    id: normalizeId(segmentValue?.id),
    value: segmentValue?.value || '',
    segmentKey: {
      id: normalizeId(segmentKey?.id),
      name: segmentKey?.name || '',
    },
  };
};

const buildParsedTargeting = (adMediaAsset, lookups) => {
  const segmentValues = asArray(adMediaAsset?.segmentValues)
    .map((segmentValue) => {
      const parsedSegmentValue = buildParsedSegmentValue(segmentValue);
      if (!parsedSegmentValue.id) return null;

      lookups.segmentValueById.set(parsedSegmentValue.id, parsedSegmentValue);
      if (parsedSegmentValue.segmentKey.id) {
        lookups.segmentKeyById.set(parsedSegmentValue.segmentKey.id, parsedSegmentValue.segmentKey);
      }
      return parsedSegmentValue;
    })
    .filter(Boolean);

  const segmentValuesBySegmentKey = segmentValues.reduce((result, segmentValue) => {
    const keyId = normalizeId(segmentValue?.segmentKey?.id);
    if (!keyId) return result;
    if (!result[keyId]) result[keyId] = [];
    result[keyId].push(segmentValue);
    return result;
  }, {});

  return {
    id: normalizeId(adMediaAsset?.id),
    name: adMediaAsset?.name || '',
    segmentValues,
    segmentValuesBySegmentKey,
  };
};

const buildSegmentValuesBySegmentKey = (adMediaAsset) => asArray(adMediaAsset?.segmentValues).reduce((result, segmentValue) => {
  const keyId = normalizeId(segmentValue?.segmentKey?.id);
  const segmentValueId = normalizeId(segmentValue?.id);
  if (!keyId || !segmentValueId) return result;
  if (!result[keyId]) result[keyId] = [];
  result[keyId].push(segmentValue);
  return result;
}, {});

const getAssetTargeting = (adMediaAsset) => {
  const segmentValues = asArray(adMediaAsset?.targeting?.segmentValues).length > 0
    ? asArray(adMediaAsset?.targeting?.segmentValues)
    : asArray(adMediaAsset?.segmentValues);

  const segmentValuesBySegmentKey = adMediaAsset?.targeting?.segmentValuesBySegmentKey
    || buildSegmentValuesBySegmentKey({ segmentValues });

  return {
    segmentValues,
    segmentValuesBySegmentKey,
  };
};

const registerAdMediaAsset = (adMediaAsset, lookups) => {
  const assetId = normalizeId(adMediaAsset?.id);
  if (!assetId) return;

  const parsedTargeting = buildParsedTargeting(adMediaAsset, lookups);
  adMediaAsset.segmentValues = parsedTargeting.segmentValues;
  adMediaAsset.targeting = parsedTargeting;
  lookups.adMediaAssetById.set(assetId, adMediaAsset);
  lookups.mediaAssetById.set(assetId, adMediaAsset);
};

const registerMediaAsset = (mediaAsset, lookups) => {
  const assetId = normalizeId(mediaAsset?.id);
  if (!assetId) return;

  if (mediaAsset?.type === 'adMediaAsset') {
    registerAdMediaAsset(mediaAsset, lookups);
    return;
  }

  lookups.mediaAssetById.set(assetId, mediaAsset);
};

const collectCampaignAdAssetIds = (campaign) => {
  const adAssetIds = [];
  asArray(campaign?.contentGroups).forEach((contentGroup) => {
    asArray(contentGroup?.contents).forEach((content) => {
      if (content?.mediaAsset?.type === 'adMediaAsset') {
        adAssetIds.push(normalizeId(content.mediaAsset.id));
      }
      asArray(content?.adMediaAssetCandidates).forEach((adMediaAsset) => {
        adAssetIds.push(normalizeId(adMediaAsset?.id));
      });
    });
  });

  return ensureArrayUnique(adAssetIds);
};

const buildCampaignQuestionnaireGroups = (campaign, questionnaireGroups, lookups) => {
  const adAssetIds = new Set(collectCampaignAdAssetIds(campaign));

  return asArray(questionnaireGroups)
    .map((group) => {
      const groupType = normalizeId(group?.groupType || group?.questionnaire?.kind);
      const questionnaire = group?.questionnaire
        ? {
          ...group.questionnaire,
          options: asArray(group.questionnaire.options).map((option) => ({
            ...option,
            segmentValueName: lookups?.segmentValueById?.get(normalizeId(option?.segmentValueId))?.value || '',
          })),
        }
        : null;
      const adMediaAssets = asArray(group?.adMediaAssets)
        .filter((adMediaAsset) => adAssetIds.has(normalizeId(adMediaAsset?.id)))
        .map((adMediaAsset) => ({
          id: normalizeId(adMediaAsset?.id),
          segmentValueIds: ensureArrayUnique(asArray(adMediaAsset?.segmentValueIds)),
        }));

      if (adMediaAssets.length === 0) return null;
      if (groupType === 'segment' && !normalizeId(group?.segmentKeyId)) return null;

      return {
        groupType,
        segmentKeyId: normalizeId(group.segmentKeyId),
        segmentKeyName: lookups?.segmentKeyById?.get(normalizeId(group?.segmentKeyId))?.name || '',
        questionnaire,
        adMediaAssets,
      };
    })
    .filter(Boolean);
};

export const normalizeStreamsTargeting = (items) => {
  if (!items || !Array.isArray(items.data)) return items;

  const lookups = createTargetingLookups();
  const questionnaireGroups = asArray(items?.questionnaireGroups);
  const frequencyInsertContents = asArray(items?.meta?.frequencyInsertContents);
  const frequencyInsertInterval = Math.max(1, Number(items?.meta?.frequencyInsertInterval || 4));

  items.questionnaireGroups = questionnaireGroups;

  items.data.forEach((campaign) => {
    const campaignId = normalizeId(campaign?.id);
    if (campaignId) lookups.campaignById.set(campaignId, campaign);
    campaign.frequencyInsertContents = frequencyInsertContents;
    campaign.frequencyInsertInterval = frequencyInsertInterval;

    asArray(campaign?.contentGroups).forEach((contentGroup) => {
      const contentGroupId = normalizeId(contentGroup?.id);
      if (contentGroupId) lookups.contentGroupById.set(contentGroupId, contentGroup);

      asArray(contentGroup?.contents).forEach((content) => {
        const contentId = normalizeId(content?.id);
        if (contentId) lookups.contentById.set(contentId, content);

        registerMediaAsset(content?.mediaAsset, lookups);
        const adMediaAssetCandidates = asArray(content?.adMediaAssetCandidates);
        adMediaAssetCandidates.forEach((adMediaAsset) => {
          registerAdMediaAsset(adMediaAsset, lookups);
        });
        if (!content?.mediaAsset && adMediaAssetCandidates.length > 0) {
          // 初回のアイコン描画では既存コードが mediaAsset を前提にしているため、先頭候補を仮採用する
          // 併せて CTA も content 直下へ写し、表示確定前でも広告CTAが空にならないようにする
          applyAdMediaAssetToContent(content, adMediaAssetCandidates[0]);
        }
      });
    });

    campaign.questionnaireGroups = buildCampaignQuestionnaireGroups(campaign, questionnaireGroups, lookups);
  });

  items.targetingLookups = lookups;
  return items;
};

const normalizeFrequencyInsertMediaAsset = (content) => {
  const mediaAsset = content?.mediaAsset || content?.media_asset;
  if (!mediaAsset) return null;

  return {
    ...mediaAsset,
    id: normalizeId(mediaAsset.id),
    type: mediaAsset.type || 'mediaAsset',
    ctaText: content?.ctaText ?? content?.cta_text ?? mediaAsset.ctaText ?? '',
    ctaLink: content?.ctaLink ?? content?.cta_link ?? mediaAsset.ctaLink ?? '',
    ctaBackgroundColorCode: content?.ctaBackgroundColorCode ?? content?.cta_background_color_code ?? mediaAsset.ctaBackgroundColorCode,
    ctaBackgroundOpacity: content?.ctaBackgroundOpacity ?? content?.cta_background_opacity ?? mediaAsset.ctaBackgroundOpacity,
    ctaTextColorCode: content?.ctaTextColorCode ?? content?.cta_text_color_code ?? mediaAsset.ctaTextColorCode,
    ctaFontSize: content?.ctaFontSize ?? content?.cta_font_size ?? mediaAsset.ctaFontSize,
  };
};

const buildFrequencyInsertContent = (candidate, sequence) => {
  const mediaAsset = normalizeFrequencyInsertMediaAsset(candidate);
  if (!mediaAsset?.id) return null;

  const id = `${FREQUENCY_INSERT_CONTENT_PREFIX}-${candidate.id || mediaAsset.id}-${sequence}`;
  return {
    id,
    isFrequencyInsertContent: true,
    frequencyInsertContentId: normalizeId(candidate.id || mediaAsset.id),
    position: sequence,
    contentGroupId: id,
    mediaAsset,
    ctaLink: mediaAsset.ctaLink || '',
    ctaText: mediaAsset.ctaText || '',
    ctaBackgroundColorCode: mediaAsset.ctaBackgroundColorCode,
    ctaBackgroundOpacity: mediaAsset.ctaBackgroundOpacity,
    ctaTextColorCode: mediaAsset.ctaTextColorCode,
    ctaFontSize: mediaAsset.ctaFontSize,
  };
};

// 広告アセットを content.mediaAsset に採用する際、CTA情報も content 直下へ写す。
// ctaButton() など描画側は content.ctaText / content.ctaLink（content直下）を参照するため、
// mediaAsset だけ差し替えると広告(非固定/ランダム枠)のCTAが空になりボタンが非表示になる。
// API(非固定広告)は content 直下の cta を意図的に nil にし、CTA実データを広告アセット側に載せるため、
// ここで採用アセットの CTA を content へ反映する（buildFrequencyInsertContent と同じ考え方）。
const applyAdMediaAssetToContent = (content, adMediaAsset) => {
  if (!content || !adMediaAsset) return content;
  content.mediaAsset = adMediaAsset;
  content.ctaText = adMediaAsset.ctaText || '';
  content.ctaLink = adMediaAsset.ctaLink || '';
  content.ctaBackgroundColorCode = adMediaAsset.ctaBackgroundColorCode;
  content.ctaBackgroundOpacity = adMediaAsset.ctaBackgroundOpacity;
  content.ctaTextColorCode = adMediaAsset.ctaTextColorCode;
  content.ctaFontSize = adMediaAsset.ctaFontSize;
  return content;
};

// seenIds: ユーザーが実際に閲覧確認済みのfreq mediaAsset ID Set（displayedIdsより優先で再表示防止に使う）
const pickFrequencyInsertCandidate = (candidates, displayedIds, seenIds) => {
  const selectableCandidates = candidates.filter((candidate) => {
    const mediaAsset = normalizeFrequencyInsertMediaAsset(candidate);
    if (!mediaAsset?.id) return false;
    // 閲覧確認済みの候補は同一キャンペーン内で絶対に再表示しない
    if (seenIds?.has(mediaAsset.id)) return false;
    return !displayedIds.has(mediaAsset.id);
  });
  return selectableCandidates[Math.floor(Math.random() * selectableCandidates.length)] || null;
};

// ─── Circle (story) 型 向け グループ内挿入 ─────────────────────────────────

/**
 * circle 型モーダル開時に頻度挿入の状態を初期化して返す。
 * 機能が無効または候補がない場合は null を返す。
 */
export const initFreqInsertCircleState = (campaign) => {
  if (campaign?.frequencyInsertEnabled === false) return null;
  const candidates = asArray(campaign?.frequencyInsertContents);
  if (candidates.length === 0) return null;
  const interval = Math.max(1, Number(campaign?.frequencyInsertInterval || 4));
  return {
    interval,
    contentsSeen: 0,
    sequence: 0,
    displayedIds: new Set(),
    // ユーザーが実際に閲覧したfreq mediaAsset IDのSet。displayedIdsと異なり削除されない（再表示防止の根拠）
    seenIds: new Set(),
    // 実際に表示された通常コンテンツのIDのSet。size が contentsSeen と一致する（重複カウント防止）。
    // 遷移時の moviePosition に依存せず表示の瞬間に積み上げるため、スワイプの仕方によらず安定する。
    seenNormalContentIds: new Set(),
    // 直近に表示されたコンテンツが freq だったか（pre-insert スキップ判定に使う。moviePosition非依存）
    lastDisplayedWasFreq: false,
    candidates,
    // 前グループの最後がfreqだった場合にpre-insertをスキップするフラグ
    skipNextPreInsert: false,
  };
};

/**
 * グループの contents 配列に頻度挿入コンテンツを適切な位置で注入した新しいグループを返す。
 * state.contentsSeen をもとに「残り N 件で挿入」を計算し、state を副作用的に更新する。
 */
export const injectFreqInsertIntoGroup = (group, state) => {
  if (!state || !group) return group;

  const { interval, contentsSeen } = state;
  const result = [];

  // 直前グループの最後がfreqだった場合はスキップ（前グループで既に挿入済みのため）
  const shouldPreInsert = !state.skipNextPreInsert && contentsSeen > 0 && contentsSeen % interval === 0;
  state.skipNextPreInsert = false;
  if (shouldPreInsert) {
    const candidate = pickFrequencyInsertCandidate(state.candidates, state.displayedIds, state.seenIds);
    if (candidate) {
      const insertContent = buildFrequencyInsertContent(candidate, state.sequence);
      if (insertContent) {
        state.displayedIds.add(normalizeId(insertContent.mediaAsset.id));
        state.sequence += 1;
        result.push(insertContent);
      }
    }
  }

  // 先頭挿入後は interval ごと、そうでなければ残りカウント後に挿入
  const nextInsertAt = shouldPreInsert ? interval : interval - (contentsSeen % interval);
  let normalCount = 0;

  asArray(group.contents).forEach((content) => {
    if (content?.isFrequencyInsertContent) return;
    result.push(content);
    normalCount += 1;

    const shouldInsert =
      normalCount === nextInsertAt
      || (normalCount > nextInsertAt && (normalCount - nextInsertAt) % interval === 0);

    if (shouldInsert) {
      const candidate = pickFrequencyInsertCandidate(state.candidates, state.displayedIds, state.seenIds);
      if (!candidate) return;
      const insertContent = buildFrequencyInsertContent(candidate, state.sequence);
      if (!insertContent) return;
      state.displayedIds.add(normalizeId(insertContent.mediaAsset.id));
      state.sequence += 1;
      result.push(insertContent);
    }
  });

  return { ...group, contents: result };
};

/**
 * コンテンツが実際に画面表示された瞬間に、頻度挿入の状態を更新する。
 * グループ離脱時の moviePosition は信頼できない（スワイプの仕方でリセット時刻が変わる）ため、
 * 表示の瞬間に記録することでカウント・閲覧判定をスワイプ操作によらず安定させる。
 *  - freq: mediaAsset ID を seenIds へ記録（同一キャンペーン内での再表示を防止）
 *  - 通常コンテンツ: ID を seenNormalContentIds へ記録し、contentsSeen をその件数に同期（重複カウントなし）
 *  - lastDisplayedWasFreq: 直近表示が freq だったかを記録（pre-insert スキップ判定に使用）
 */
export const recordDisplayedContent = (content, state) => {
  if (!state || !content) return;
  // モバイルのみ表示時カウントを採用する。デスクトップ円形は遷移時カウント（contentsSeen +=）を
  // 使い続けるため、ここで contentsSeen を触ると二重カウントになる。seenIds / lastDisplayedWasFreq は両対応。
  const countAtDisplayTime = window.storeeelItems?.isMobile === true;
  if (content.isFrequencyInsertContent) {
    const mediaId = normalizeId(content.mediaAsset?.id);
    if (mediaId && state.seenIds) state.seenIds.add(mediaId);
    state.lastDisplayedWasFreq = true;
    return;
  }
  if (countAtDisplayTime) {
    const contentId = normalizeId(content.id);
    if (contentId && state.seenNormalContentIds && !state.seenNormalContentIds.has(contentId)) {
      state.seenNormalContentIds.add(contentId);
      state.contentsSeen = state.seenNormalContentIds.size;
    }
  }
  state.lastDisplayedWasFreq = false;
};

// 後方互換: freq 専用の旧名。recordDisplayedContent に委譲する。
export const recordSeenFreqContent = (content, state) => recordDisplayedContent(content, state);

/**
 * グループの contents[0..moviePosition] に含まれる通常コンテンツ（頻度挿入でない）の数を返す。
 * グループ離脱時に state.contentsSeen を更新するために使う。
 */
export const countNormalContentsSeen = (group, moviePosition) => {
  if (!group || !Array.isArray(group.contents)) return 0;
  const pos = Number.isFinite(moviePosition) && moviePosition >= 0 ? moviePosition : 0;
  return group.contents.slice(0, pos + 1).filter((c) => !c?.isFrequencyInsertContent).length;
};

// グループ離脱時に、ユーザーが到達していない位置のfreq挿入コンテンツを除去する。
// displayedIds から除去して次グループで再利用できるようにし、
// さらに group.contents からも物理削除することで戻り時に表示されないようにする。
// 到達済み位置（pos 以前）のfreq IDは seenIds に追記して、再表示を永続的に防止する。
export const unmarkUnseenFreqInserts = (group, moviePosition, state) => {
  if (!state || !group) return;
  const pos = Number.isFinite(moviePosition) && moviePosition >= 0 ? moviePosition : 0;
  if (!Array.isArray(group.contents)) return;

  // ユーザーが実際に見た範囲（pos以前）のfreq IDをseenIdsに記録する
  // seenIdsは削除されないため、moviePositionが誤ってリセットされても再表示を防げる
  if (state.seenIds) {
    group.contents.slice(0, pos + 1).forEach((content) => {
      if (content?.isFrequencyInsertContent) {
        const id = normalizeId(content.mediaAsset?.id);
        if (id) state.seenIds.add(id);
      }
    });
  }

  group.contents.slice(pos + 1).forEach((content) => {
    if (content?.isFrequencyInsertContent) {
      const id = normalizeId(content.mediaAsset?.id);
      if (id) state.displayedIds.delete(id);
    }
  });
  group.contents = group.contents.filter(
    (content, index) => index <= pos || !content?.isFrequencyInsertContent,
  );
};

// ─────────────────────────────────────────────────────────────────────────────

export const applyFrequencyInsertContents = (campaign, contentGroups, startGroupIndex = 0) => {
  if (campaign?.frequencyInsertEnabled === false) return contentGroups;

  const candidates = asArray(campaign?.frequencyInsertContents);
  const interval = Math.max(1, Number(campaign?.frequencyInsertInterval || 4));
  if (candidates.length === 0) return contentGroups;

  let normalContentDisplayCount = 0;
  let insertSequence = 0;
  const displayedMediaAssetIds = new Set();
  const result = [];

  contentGroups.forEach((contentGroup) => {
    result.push(contentGroup);

    asArray(contentGroup?.contents).forEach((content) => {
      if (content?.isFrequencyInsertContent) return;

      normalContentDisplayCount += 1;
      // 開始位置より前はカウントのみ（挿入しない）
      if (normalContentDisplayCount <= startGroupIndex) return;
      if ((normalContentDisplayCount - startGroupIndex) % interval !== 0) return;

      const candidate = pickFrequencyInsertCandidate(candidates, displayedMediaAssetIds);
      const insertContent = candidate ? buildFrequencyInsertContent(candidate, insertSequence) : null;
      if (!insertContent) return;

      displayedMediaAssetIds.add(normalizeId(insertContent.mediaAsset.id));
      insertSequence += 1;
      // SP・PC 共通で contents[0] または flatMap に乗るよう独立したグループとして挿入
      result.push({ id: insertContent.id, contents: [insertContent] });
    });
  });

  return result;
};

// 回答のキーになる segmentKeyId を持たない設問はセグメント設問ではない。
// タグ機能廃止前の応答（groupType: 'tag'）が残っていると、回答キーが無いまま
// 「未回答」と判定され続け、アンケートが出しっぱなしになったうえ
// matchesQuestionnaireAnswers 側でも広告が永久に非表示になる。
// 旧応答はターゲティング対象として扱わず、素通りさせる。
const isSegmentQuestionnaireGroup = (group) => Boolean(normalizeId(group?.segmentKeyId));

export const matchesQuestionnaireAnswers = (adMediaAssetId, questionnaireGroups, answers) => {
  const normalizedAssetId = normalizeId(adMediaAssetId);
  if (!normalizedAssetId) return true;
  const normalizedAnswers = normalizeTargetingAnswers(answers);

  return asArray(questionnaireGroups).every((group) => {
    if (!isSegmentQuestionnaireGroup(group)) return true;

    const assetRule = asArray(group?.adMediaAssets)
      .find((adMediaAsset) => normalizeId(adMediaAsset?.id) === normalizedAssetId);
    if (!assetRule) return true;

    const answeredSegmentValueId = normalizeId(normalizedAnswers.segments?.[group.segmentKeyId]);
    if (!answeredSegmentValueId) return false;

    return ensureArrayUnique(asArray(assetRule.segmentValueIds)).includes(answeredSegmentValueId);
  });
};

export const getPendingQuestionnaireGroups = (campaign, answers = {}) => {
  const normalizedAnswers = normalizeTargetingAnswers(answers);

  return asArray(campaign?.questionnaireGroups)
    .filter((group) => (
      isSegmentQuestionnaireGroup(group)
      && !normalizeId(normalizedAnswers.segments?.[group.segmentKeyId])
    ));
};

const collectTargetingAssetIdsFromContent = (content) => {
  const assetIds = [];

  if (content?.mediaAsset?.type === 'adMediaAsset') {
    assetIds.push(normalizeId(content.mediaAsset.id));
  }
  asArray(content?.adMediaAssetCandidates).forEach((adMediaAsset) => {
    assetIds.push(normalizeId(adMediaAsset?.id));
  });

  return ensureArrayUnique(assetIds);
};

export const getPendingQuestionnaireGroupsForContents = (campaign, contents, answers = {}) => {
  const assetIdSet = new Set(
    ensureArrayUnique(
      asArray(contents).flatMap((content) => collectTargetingAssetIdsFromContent(content))
    )
  );
  if (assetIdSet.size === 0) return [];

  return getPendingQuestionnaireGroups(campaign, answers)
    .filter((group) => asArray(group?.adMediaAssets).some((adMediaAsset) => assetIdSet.has(normalizeId(adMediaAsset?.id))));
};

export const getPendingQuestionnaireGroupsForContent = (campaign, content, answers = {}) => (
  getPendingQuestionnaireGroupsForContents(campaign, [content], answers)
);

export const restoreTargetingAnswersFromStorage = (campaign, storedAnswers = {}) => {
  const questionnaireGroups = asArray(campaign?.questionnaireGroups);
  const storedSegments = normalizeStoredAnswerValues(storedAnswers?.segments);
  const storedSegmentsByQuestion = normalizeStoredAnswerMap(storedAnswers?.segments);

  return questionnaireGroups.reduce((result, group) => {
    const storedSegmentCandidates = storedSegmentsByQuestion[normalizeId(group?.segmentKeyId)]
      || storedSegmentsByQuestion[normalizeId(group?.questionnaire?.id)]
      || storedSegments;
    const storedSegmentValue = storedSegmentCandidates.find((storedValue) => findSegmentOptionByStoredValue(group, storedValue));
    const matchedOption = findSegmentOptionByStoredValue(group, storedSegmentValue);
    if (matchedOption && group?.segmentKeyId) {
      result.segments[group.segmentKeyId] = normalizeId(matchedOption.segmentValueId);
    }
    return result;
  }, { segments: {} });
};

export const serializeTargetingAnswersForStorage = (campaign, answers = {}) => {
  const normalizedAnswers = normalizeTargetingAnswers(answers);
  const questionnaireGroups = asArray(campaign?.questionnaireGroups);

  return questionnaireGroups.reduce((result, group) => {
    const segmentValueId = normalizeId(normalizedAnswers.segments?.[group?.segmentKeyId]);
    if (!segmentValueId) return result;

    const option = asArray(group?.questionnaire?.options)
      .find((item) => normalizeId(item?.segmentValueId) === segmentValueId);
    const storageValue = getSegmentOptionStorageValue(option);
    if (storageValue) {
      result.segments[normalizeId(group?.segmentKeyId)] = storageValue;
    }
    return result;
  }, { segments: {} });
};

const buildAnswerRevision = (campaign, answers = {}) => {
  const normalizedAnswers = normalizeTargetingAnswers(answers);
  const questionnaireGroups = asArray(campaign?.questionnaireGroups);

  const segments = questionnaireGroups
    .filter((group) => group?.groupType === 'segment')
    .map((group) => {
      const segmentKeyId = normalizeId(group?.segmentKeyId);
      const segmentValueId = normalizeId(normalizedAnswers?.segments?.[segmentKeyId]);
      return segmentKeyId && segmentValueId ? `${segmentKeyId}=${segmentValueId}` : '';
    })
    .filter(Boolean)
    .sort();

  return `segments:${segments.join('|')}`;
};

const createSessionAssignmentController = (campaign, answers = {}, options = {}) => {
  const campaignId = normalizeId(campaign?.id);
  if (!campaignId) {
    return {
      save: () => {},
    };
  }

  const tenantId = normalizeId(options?.tenantId);
  const currentUrl = normalizeId(options?.currentUrl);
  const answerRevision = buildAnswerRevision(campaign, answers);
  const state = readRuntimeAssignments();
  const initializedState = state && (
    state.tenantId !== tenantId
    || state.currentUrl !== currentUrl
  )
    ? {
      version: TARGETING_SESSION_STORAGE_VERSION,
      tenantId,
      currentUrl,
      campaigns: {},
    }
    : {
      ...(state || {}),
      version: TARGETING_SESSION_STORAGE_VERSION,
      tenantId,
      currentUrl,
      campaigns: state?.campaigns && typeof state.campaigns === 'object' ? state.campaigns : {},
    };

  const previousCampaignState = initializedState.campaigns[campaignId];
  const shouldResetCampaignState = options?.resetSessionAssignments
    || previousCampaignState?.answerRevision !== answerRevision;

  const campaignState = shouldResetCampaignState
    ? {
      answerRevision,
      stickySlots: previousCampaignState?.stickySlots && typeof previousCampaignState.stickySlots === 'object'
        ? { ...previousCampaignState.stickySlots }
        : {},
      displayedAssetIds: [],
      displayCounts: {},
      displayOrders: {},
      nextDisplayOrder: 1,
    }
    : {
      answerRevision,
      stickySlots: previousCampaignState?.stickySlots && typeof previousCampaignState.stickySlots === 'object'
        ? { ...previousCampaignState.stickySlots }
        : {},
      displayedAssetIds: normalizeStoredAssetIds(previousCampaignState?.displayedAssetIds),
      displayCounts: normalizeStoredDisplayCounts(previousCampaignState?.displayCounts),
      displayOrders: normalizeStoredDisplayOrders(previousCampaignState?.displayOrders),
      nextDisplayOrder: Number(previousCampaignState?.nextDisplayOrder) > 0 ? Number(previousCampaignState.nextDisplayOrder) : 1,
    };

  initializedState.campaigns[campaignId] = campaignState;
  const selectedAssetIds = new Set();
  const displayedAssetIds = new Set(normalizeStoredAssetIds(campaignState.displayedAssetIds));
  const displayCounts = { ...normalizeStoredDisplayCounts(campaignState.displayCounts) };
  const displayOrders = { ...normalizeStoredDisplayOrders(campaignState.displayOrders) };
  let nextDisplayOrder = Number(campaignState.nextDisplayOrder) > 0
    ? Number(campaignState.nextDisplayOrder)
    : (Math.max(0, ...Object.values(displayOrders)) + 1);

  const buildSlotKey = ({ groupId, contentId }) => {
    const normalizedGroupId = normalizeId(groupId);
    const normalizedContentId = normalizeId(contentId);
    return [tenantId, currentUrl, campaignId, normalizedGroupId, normalizedContentId].join(':');
  };

  return {
    getStickyAssignedAssetId: ({ groupId, contentId }) => normalizeId(campaignState.stickySlots?.[buildSlotKey({ groupId, contentId })]),
    hasSelectedAssetId: (adMediaAssetId) => selectedAssetIds.has(normalizeId(adMediaAssetId)),
    hasDisplayedAssetId: (adMediaAssetId) => displayedAssetIds.has(normalizeId(adMediaAssetId)),
    getDisplayCount: (adMediaAssetId) => displayCounts[normalizeId(adMediaAssetId)] || 0,
    getDisplayOrder: (adMediaAssetId) => displayOrders[normalizeId(adMediaAssetId)] || 0,
    getLastDisplayedAssetId: () => Object.entries(displayOrders)
      .sort((left, right) => right[1] - left[1])[0]?.[0] || '',
    recordDisplayedAssetId: (adMediaAssetId) => {
      const normalizedAssetId = normalizeId(adMediaAssetId);
      if (!normalizedAssetId) return;
      selectedAssetIds.add(normalizedAssetId);
      displayedAssetIds.add(normalizedAssetId);
      displayCounts[normalizedAssetId] = (displayCounts[normalizedAssetId] || 0) + 1;
      displayOrders[normalizedAssetId] = nextDisplayOrder;
      nextDisplayOrder += 1;
    },
    setStickyAssignedAssetId: ({ groupId, contentId }, adMediaAssetId) => {
      const slotKey = buildSlotKey({ groupId, contentId });
      const normalizedAssetId = normalizeId(adMediaAssetId);
      if (!slotKey || !normalizedAssetId) return;
      campaignState.stickySlots[slotKey] = normalizedAssetId;
    },
    save: () => {
      campaignState.stickySlots = {
        ...(campaignState.stickySlots && typeof campaignState.stickySlots === 'object' ? campaignState.stickySlots : {}),
      };
      campaignState.displayedAssetIds = [...displayedAssetIds];
      campaignState.displayCounts = { ...displayCounts };
      campaignState.displayOrders = { ...displayOrders };
      campaignState.nextDisplayOrder = nextDisplayOrder;
      initializedState.campaigns[campaignId] = campaignState;
      writeRuntimeAssignments(initializedState);
    },
  };
};

const matchesSegmentAnswersForAsset = (adMediaAsset, answers) => {
  const normalizedAnswers = normalizeTargetingAnswers(answers);
  const targeting = getAssetTargeting(adMediaAsset);
  const segmentValuesBySegmentKey = targeting.segmentValuesBySegmentKey || {};
  const segmentKeyIds = Object.keys(segmentValuesBySegmentKey);
  if (segmentKeyIds.length === 0) return true;

  return segmentKeyIds.every((segmentKeyId) => {
    const answeredSegmentValueId = normalizeId(normalizedAnswers?.segments?.[segmentKeyId]);
    if (!answeredSegmentValueId) return false;

    return asArray(segmentValuesBySegmentKey[segmentKeyId]).some((segmentValue) => (
      normalizeId(segmentValue?.id) === answeredSegmentValueId
    ));
  });
};

// プロモーション（頻度差し込み）の候補をユーザーのセグメント回答で絞る。
// 広告コンテンツは非固定広告と同じ条件で扱い、セグメント指定に一致したときだけ差し込む
// （指定があるのに未回答なら出さない）。自社コンテンツはセグメントの概念がないため常に対象。
// 絞った結果が空になった場合は候補なしとなり、その回の差し込み自体が起きない。
const filterFrequencyInsertCandidatesByAnswers = (candidates, answers) => (
  asArray(candidates).filter((candidate) => {
    const mediaAsset = candidate?.mediaAsset || candidate?.media_asset;
    if (mediaAsset?.type !== 'adMediaAsset') return true;

    return matchesSegmentAnswersForAsset(mediaAsset, answers);
  })
);

// キャンペーンの固定枠・ランダム枠で表示する広告は、同じキャンペーンのプロモーションから外す。
// 同一キャンペーン内で同じ広告コンテンツが2回出るのを防ぐ（自社コンテンツは対象外）。
// 突き合わせにはキャンペーン側と同じ数値IDを使う。プロモーションの mediaAsset.id は
// GA4 の media_id に使う hashid で名前空間が違うため、API が別キーで渡す adMediaAssetId を見る。
// adMediaAssetId を持たない候補（新キーを投影する前のD1など）は従来どおり通す。
const filterFrequencyInsertCandidatesByCampaignAdAssetIds = (candidates, campaignAdAssetIds = []) => {
  const normalizedCampaignAdAssetIds = ensureArrayUnique(campaignAdAssetIds);
  if (normalizedCampaignAdAssetIds.length === 0) return asArray(candidates);

  return asArray(candidates).filter((candidate) => {
    const mediaAsset = candidate?.mediaAsset || candidate?.media_asset;
    if (mediaAsset?.type !== 'adMediaAsset') return true;

    const adMediaAssetId = normalizeId(mediaAsset?.adMediaAssetId ?? mediaAsset?.ad_media_asset_id);
    if (!adMediaAssetId) return true;

    return !normalizedCampaignAdAssetIds.includes(adMediaAssetId);
  });
};

const filterCandidateEntriesByExcludedAssetIds = (candidateEntries, excludedAssetIds = []) => {
  const normalizedExcludedAssetIds = ensureArrayUnique(excludedAssetIds);
  if (normalizedExcludedAssetIds.length === 0) return candidateEntries;

  // ランダム広告は、キャンペーン内で既に使う広告コンテンツを候補から外す。
  return candidateEntries.filter((candidate) => (
    !normalizedExcludedAssetIds.includes(normalizeId(candidate?.adMediaAsset?.id))
  ));
};

const pickRandomCandidateEntry = (candidateEntries, lastDisplayedAssetId = '') => {
  if (candidateEntries.length <= 1) return candidateEntries[0] || null;

  const normalizedLastDisplayedAssetId = normalizeId(lastDisplayedAssetId);
  const candidatePool = normalizedLastDisplayedAssetId
    ? candidateEntries.filter((candidate) => normalizeId(candidate?.adMediaAsset?.id) !== normalizedLastDisplayedAssetId)
    : candidateEntries;
  const selectableEntries = candidatePool.length > 0 ? candidatePool : candidateEntries;

  return selectableEntries[Math.floor(Math.random() * selectableEntries.length)] || null;
};

const selectCandidateEntry = (candidateEntries, lastDisplayedAssetId, excludedAssetIds = []) => {
  const candidatesAvoidingAdjacentDuplicates = filterCandidateEntriesByExcludedAssetIds(
    candidateEntries,
    excludedAssetIds
  );
  const candidatesExcludingLastDisplayed = normalizeId(lastDisplayedAssetId)
    ? candidatesAvoidingAdjacentDuplicates.filter((candidate) => (
      normalizeId(candidate?.adMediaAsset?.id) !== normalizeId(lastDisplayedAssetId)
    ))
    : candidatesAvoidingAdjacentDuplicates;
  const selectableCandidates = candidatesExcludingLastDisplayed.length > 0
    ? candidatesExcludingLastDisplayed
    : candidatesAvoidingAdjacentDuplicates;
  // 完全ランダムを保ちつつ、一巡するまでは未表示候補を優先する
  const minimumDisplayCount = Math.min(...selectableCandidates.map((candidate) => candidate.displayCount));
  const leastDisplayedCandidates = selectableCandidates.filter((candidate) => candidate.displayCount === minimumDisplayCount);
  return pickRandomCandidateEntry(leastDisplayedCandidates);
};

const shouldKeepInitialDisplayedAd = (options = {}) => {
  const frame = normalizeId(options?.frame);
  // タイル型はリール型とビューア挙動を共有するため、初回表示広告の維持も同じ扱いにする
  if (isReelLike(frame)) return true;
  if (frame === 'circle') return Number(options?.contentIndex) === 0;
  return false;
};

// ストーリー型のグループ1枚目。アイコンのサムネイルと【広告】ラベルになる枠なので、
// ページ表示時に決めた広告をセッション中固定し、モーダルで開いた中身と一致させる。
const isCircleGroupHeadAdSlot = (options = {}) => (
  normalizeId(options?.frame) === 'circle' && Number(options?.contentIndex) === 0
);

const isRandomAdContent = (content) => asArray(content?.adMediaAssetCandidates).length > 0;

const getDisplayedFixedAdAssetId = (content) => {
  const mediaAssetId = normalizeId(content?.mediaAsset?.id);
  if (!mediaAssetId) return '';
  if (isRandomAdContent(content)) return '';
  if (content?.mediaAsset?.type !== 'adMediaAsset') return '';
  return mediaAssetId;
};

const getRenderableFixedAdAssetId = (content, answers) => {
  const fixedAdAssetId = getDisplayedFixedAdAssetId(content);
  if (!fixedAdAssetId) return '';
  return matchesSegmentAnswersForAsset(content?.mediaAsset, answers) ? fixedAdAssetId : '';
};

const collectRenderableFixedAdAssetIds = (contentGroups, answers) => ensureArrayUnique(
  asArray(contentGroups).flatMap((contentGroup) => (
    asArray(contentGroup?.contents).map((content) => getRenderableFixedAdAssetId(content, answers))
  ))
);

const resolveContentForDisplay = (content, answers, options = {}) => {
  const adCandidates = asArray(content?.adMediaAssetCandidates);
  if (adCandidates.length > 0) {
    const shouldPersistInitialDisplay = shouldKeepInitialDisplayedAd(options);
    const stickyAssignedAssetId = shouldPersistInitialDisplay
      ? options?.sessionAssignments?.getStickyAssignedAssetId?.({
        groupId: options?.groupId,
        contentId: content?.id,
      })
      : '';
    const stickyCandidate = stickyAssignedAssetId
      ? adCandidates.find((adMediaAsset) => normalizeId(adMediaAsset?.id) === stickyAssignedAssetId) || null
      : null;
    if (
      stickyCandidate
      && !asArray(options?.excludedAssetIds).includes(normalizeId(stickyCandidate?.id))
      // セグメントは「その人に出してよいか」の判定なので、固定より優先する。
      // 回答が変わって対象外になった広告を固定で出し続けないようにする。
      && matchesSegmentAnswersForAsset(stickyCandidate, answers)
    ) {
      // 初回表示した広告は、アンケート回答後も同じ枠で維持する。
      content.adMediaAssetCandidates = adCandidates;
      applyAdMediaAssetToContent(content, stickyCandidate);
      return content;
    }

    const matchedCandidates = adCandidates.filter((adMediaAsset) => matchesSegmentAnswersForAsset(adMediaAsset, answers));
    if (matchedCandidates.length === 0) return null;
    const filterByExcludedAssetIds = (excludedAssetIds) => filterCandidateEntriesByExcludedAssetIds(
      matchedCandidates.map((adMediaAsset) => ({ adMediaAsset })),
      excludedAssetIds || []
    ).map((candidate) => candidate.adMediaAsset);

    // 重複回避を最優先する。候補を使い切った枠は表示しない（グループ1枚目も例外にしない）。
    const selectableCandidates = filterByExcludedAssetIds(options?.excludedAssetIds);
    if (selectableCandidates.length === 0) return null;

    // ストーリー型のグループ1枚目は、ページ表示時に決めた広告をセッション中固定する。
    // アイコンのサムネイル・【広告】ラベルと、モーダルで開いた中身を一致させるため。
    const rememberIfInitialDisplaySlot = (selectedAdMediaAssetId) => {
      if (!isCircleGroupHeadAdSlot(options)) return;
      options?.sessionAssignments?.setStickyAssignedAssetId?.({
        groupId: options?.groupId,
        contentId: content?.id,
      }, selectedAdMediaAssetId);
    };

    // セグメントは表示可否の判定だけに使い、表示順はランダム（一巡するまでは未表示を優先）。
    const candidateEntries = selectableCandidates.map((adMediaAsset) => ({
      adMediaAsset,
      displayCount: options?.sessionAssignments?.getDisplayCount?.(adMediaAsset?.id) || 0,
    }));
    const selectedCandidate = selectCandidateEntry(
      candidateEntries,
      options?.sessionAssignments?.getLastDisplayedAssetId?.() || ''
    )?.adMediaAsset || null;
    if (!selectedCandidate) return null;

    options?.sessionAssignments?.recordDisplayedAssetId?.(selectedCandidate?.id);
    rememberIfInitialDisplaySlot(selectedCandidate?.id);
    content.adMediaAssetCandidates = matchedCandidates;
    applyAdMediaAssetToContent(content, selectedCandidate);
    return content;
  }

  if (content?.mediaAsset?.type === 'adMediaAsset') {
    if (!matchesSegmentAnswersForAsset(content.mediaAsset, answers)) {
      return null;
    }
  }

  return content;
};

// コンテンツの解決順（グループ順・コンテンツ順の座標リスト）を作る。
// ストーリー型では枠を2段階に分けて解決する。
//
// 1段階目: グループ1個目の広告枠を、常にキャンペーン順で先に確定させる。
//   1個目はアイコンのサムネイル・【広告】ラベルになる枠なので、2個目以降の枠に
//   広告を先取りされて枠ごと落ちることがないようにする（開始グループ指定でも動かさない）。
// 2段階目: 余った広告を2個目以降の枠へ配る。開始グループが指定されていれば
//   「開いたグループ起点の巡回順」（開始 -> 末尾 -> 先頭 -> 開始直前）で解決し、
//   タグ一致優先が視聴地点のグループに効くようにする。
//
// リール型・タイル型はキャンペーン全体を一度に解決するため、並べ替えは行わない。
const buildContentResolutionOrder = (sourceContentGroups, options = {}) => {
  const slotsByGroup = sourceContentGroups.map((contentGroup, groupIndex) => (
    asArray(contentGroup?.contents).map((content, contentIndex) => ({ groupIndex, contentIndex, content }))
  ));
  if (normalizeId(options?.frame) !== 'circle') return slotsByGroup.flat();

  const startGroupId = normalizeId(options?.startGroupId);
  const startGroupIndex = startGroupId
    ? sourceContentGroups.findIndex((contentGroup) => normalizeId(contentGroup?.id) === startGroupId)
    : -1;
  // 開始グループが先頭（または未指定・該当なし）なら巡回させる意味がないためキャンペーン順のまま
  const restSlotsByGroup = startGroupIndex > 0
    ? [...slotsByGroup.slice(startGroupIndex), ...slotsByGroup.slice(0, startGroupIndex)]
    : slotsByGroup;

  return [
    ...slotsByGroup.flat().filter((slot) => slot.contentIndex === 0),
    ...restSlotsByGroup.flat().filter((slot) => slot.contentIndex > 0),
  ];
};

export const prepareCampaignForDisplay = (campaign, answers = {}, options = {}) => {
  const clonedCampaign = cloneValue(campaign);
  const sessionAssignments = createSessionAssignmentController(clonedCampaign, answers, options);
  // タイル型は行(tileRow)をまたいだナビゲーション順を見た目のグリッド（左→右、上→下）と一致させる
  const sourceContentGroups = clonedCampaign?.frame === 'tile'
    ? sortContentGroupsForTileReadingOrder(asArray(clonedCampaign?.contentGroups))
    : asArray(clonedCampaign?.contentGroups);
  const fixedAdAssetIds = collectRenderableFixedAdAssetIds(sourceContentGroups, answers);
  const selectedRandomAdAssetIds = new Set();
  const resolvedContentsByGroupIndex = sourceContentGroups.map(() => []);

  // タイル型・リール型は「1セル/1サムネイル＝1グループ」なので、フィードフォルダは
  // content ではなく content_group 側をN個へ仮想展開する（下の preparedGroups で処理）。
  // ストーリー型（circle）は「1サークル内を送って複数コンテンツを見る」形式のため content 展開のまま。
  const expandsAsGroups = expandsFeedFolderAsGroups(clonedCampaign);

  buildContentResolutionOrder(sourceContentGroups, {
    frame: clonedCampaign?.frame,
    startGroupId: options?.startGroupId,
  }).forEach(({ groupIndex, contentIndex, content }) => {
    if (content?.kind === 'feed_folder') {
      // グループ側で仮想展開するフレームでは、ここでは何もしない（二重抽選を避ける）
      if (expandsAsGroups) return;
      // ストーリー型: フィードフォルダは1content→N件（抽選済み商品）に展開する。
      // 既存の resolveContentForDisplay（1content→0/1件）は変更せず、新規分岐として並置する。
      // 抽選はページ表示ごとに1回。sessionOwner にはクローン前の campaign を渡す
      // （clonedCampaign に持たせると描画のたびに新しいオブジェクトになり固定が効かない）
      const resolvedProducts = resolveFeedFolderProductsOnce(content, clonedCampaign, campaign);
      if (resolvedProducts.length === 0) return;
      // 1スロットに配列を入れ、グループ組み立て時に flat() で展開する
      resolvedContentsByGroupIndex[groupIndex][contentIndex] = buildFeedFolderDisplayContents(content, resolvedProducts);
      return;
    }

    const excludedAssetIds = isRandomAdContent(content)
      ? ensureArrayUnique([...fixedAdAssetIds, ...selectedRandomAdAssetIds])
      : [];

    const resolvedContent = resolveContentForDisplay(content, answers, {
      frame: clonedCampaign?.frame,
      groupId: sourceContentGroups[groupIndex]?.id,
      contentIndex,
      sessionAssignments,
      excludedAssetIds,
    });
    if (!resolvedContent) return;

    if (isRandomAdContent(content)) {
      selectedRandomAdAssetIds.add(normalizeId(resolvedContent?.mediaAsset?.id));
    }
    // 解決順が入れ替わっても、表示は元のコンテンツ順を保つ
    resolvedContentsByGroupIndex[groupIndex][contentIndex] = resolvedContent;
  });

  const preparedGroups = sourceContentGroups.reduce((preparedContentGroups, contentGroup, groupIndex) => {
    // タイル型・リール型: フィードフォルダグループ（1グループ1件のfeed_folder content、DD-2の専有制約に
    // より他contentとの共存はない）は、1商品=1仮想content_groupのN個に展開する（DD-12 仮想タイル展開）。
    if (expandsAsGroups) {
      const feedFolderContent = asArray(contentGroup?.contents).find((content) => content?.kind === 'feed_folder');
      if (feedFolderContent) {
        const virtualGroups = buildVirtualGroupsForFeedFolder(feedFolderContent, clonedCampaign, {
          name: contentGroup?.name,
          // 仮想グループIDを描画ごとに変えないため、抽選結果をクローン前のcampaignに固定する。
          // ここが変わるとDOMのタイルとpreparedキャッシュのIDが食い違い、クリックが無反応になる。
          sessionOwner: campaign,
        });
        preparedContentGroups.push(...virtualGroups);
        return preparedContentGroups;
      }
    }

    // 疎配列の空き（表示対象外になったコンテンツ）は filter で詰める。
    // フィードフォルダのスロットだけは配列（N件）なので flat() で展開する。
    const preparedContents = resolvedContentsByGroupIndex[groupIndex].filter(Boolean).flat();
    if (preparedContents.length === 0) return preparedContentGroups;

    contentGroup.contents = preparedContents;
    preparedContentGroups.push(contentGroup);
    return preparedContentGroups;
  }, []);

  clonedCampaign.contentGroups = preparedGroups;
  // 差し込み候補もセグメントで絞る。initFreqInsertCircleState / applyFrequencyInsertContents は
  // どちらも campaign.frequencyInsertContents を読むため、ここで絞れば挿入経路すべてに一括で効く。
  clonedCampaign.frequencyInsertContents = filterFrequencyInsertCandidatesByCampaignAdAssetIds(
    filterFrequencyInsertCandidatesByAnswers(
      clonedCampaign?.frequencyInsertContents,
      answers,
    ),
    // 固定枠は表示対象のもの、ランダム枠はこの描画で実際に選ばれたものだけを除外対象にする
    ensureArrayUnique([...fixedAdAssetIds, ...selectedRandomAdAssetIds]),
  );

  sessionAssignments.save();
  return clonedCampaign;
};

export const rememberStickyInitialAdAssignment = (campaign, answers = {}, assignment = {}, options = {}) => {
  const groupId = normalizeId(assignment?.groupId);
  const contentId = normalizeId(assignment?.contentId);
  const mediaAssetId = normalizeId(assignment?.mediaAssetId);
  if (!groupId || !contentId || !mediaAssetId) return;

  const sessionAssignments = createSessionAssignmentController(campaign, answers, options);
  sessionAssignments.setStickyAssignedAssetId({ groupId, contentId }, mediaAssetId);
  sessionAssignments.save();
};

